import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Kbd } from './kbd';
import { VARIABLE_CATEGORIES } from '../../constants/templateVariables';

interface Variable {
  key: string;
  label: string;
}

interface VariableCategory {
  id: string;
  label: string;
  emailOnly?: boolean;
  variables: Variable[];
}

interface VariableCommandMenuProps {
  /** Search query (the text after '/' trigger) */
  query?: string;
  /** Callback when a variable is selected */
  onSelect: (variableKey: string) => void;
  /** Filter out email-only variables for SMS */
  isEmail?: boolean;
  /** Custom className */
  className?: string;
}

export interface VariableCommandMenuHandle {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

export const VariableCommandMenu = forwardRef<VariableCommandMenuHandle, VariableCommandMenuProps>(
  ({ query = '', onSelect, isEmail = true, className }, ref) => {
    const [search, setSearch] = useState(query);
    const commandRef = useRef<HTMLDivElement>(null);

    // Update search when query prop changes
    useEffect(() => {
      setSearch(query);
    }, [query]);

    // Filter categories based on email/SMS context
    const availableCategories: VariableCategory[] = VARIABLE_CATEGORIES.filter(
      (cat) => !cat.emailOnly || isEmail
    );

    // Filter variables based on search query
    const filteredCategories = search
      ? availableCategories
          .map((cat) => ({
            ...cat,
            variables: cat.variables.filter(
              (v) =>
                v.label.toLowerCase().includes(search.toLowerCase()) ||
                v.key.toLowerCase().includes(search.toLowerCase())
            ),
          }))
          .filter((cat) => cat.variables.length > 0)
      : availableCategories;

    const handleSelect = useCallback(
      (variableKey: string) => {
        onSelect(variableKey);
      },
      [onSelect]
    );

    // Expose keyboard handler to parent
    useImperativeHandle(ref, () => ({
      onKeyDown: (event: KeyboardEvent) => {
        if (!commandRef.current) return false;

        // Handle keyboard navigation
        if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
          // Dispatch the event to the Command component
          const keyboardEvent = new KeyboardEvent('keydown', {
            key: event.key,
            code: event.code,
            keyCode: event.keyCode,
            which: event.which,
            bubbles: true,
            cancelable: true,
          });
          commandRef.current.dispatchEvent(keyboardEvent);
          return true; // Prevent default editor behavior
        }

        return false;
      },
    }));

    return (
      <Command ref={commandRef} className={className} shouldFilter={false}>
        <CommandInput
          placeholder="Search variables..."
          value={search}
          onValueChange={setSearch}
        />
        <CommandList>
          <CommandEmpty>No variables found.</CommandEmpty>
          {filteredCategories.map((category) => (
            <CommandGroup key={category.id} heading={category.label}>
              {category.variables.map((variable) => (
                <CommandItem
                  key={variable.key}
                  value={variable.key}
                  onSelect={() => handleSelect(variable.key)}
                  className="flex items-center justify-between"
                >
                  <span>{variable.label}</span>
                  <span className="text-xs text-muted-foreground font-mono">
                    {`{{${variable.key}}}`}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
        <div className="border-t border-border px-2 py-1.5 text-xs text-muted-foreground flex items-center gap-2">
          <Kbd>Enter</Kbd> to insert • <Kbd>Esc</Kbd> to cancel
        </div>
      </Command>
    );
  }
);

VariableCommandMenu.displayName = 'VariableCommandMenu';
